import { useState, useCallback } from "react";
import { useFeedbackStore } from "../../stores/feedback.js";
import { useSettingsStore } from "../../stores/settings.js";
import {
  FeedbackToolbar,
  type FeedbackViewMode,
} from "../feedback/FeedbackToolbar.js";
import { FeedbackTimeline } from "../feedback/FeedbackTimeline.js";
import { FeedbackSwimlane } from "../feedback/FeedbackSwimlane.js";
import { FeedbackMessage } from "../feedback/FeedbackMessage.js";

export function FeedbackPanel() {
  const defaultView = useSettingsStore((s) => s.defaultFeedbackView);
  const [viewMode, setViewMode] = useState<FeedbackViewMode>(defaultView);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const messages = useFeedbackStore((s) => s.messages);
  const clear = useFeedbackStore((s) => s.clear);

  const toggleFilter = useCallback((type: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  return (
    <div className="h-full flex flex-col bg-surface/80 backdrop-blur-sm">
      <FeedbackToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        activeFilters={activeFilters}
        onToggleFilter={toggleFilter}
        search={search}
        onSearchChange={setSearch}
        messageCount={messages.length}
        onClear={clear}
      />

      <div className="flex-1 min-h-0">
        {viewMode === "timeline" && (
          <FeedbackTimeline filters={activeFilters} search={search} />
        )}
        {viewMode === "swimlane" && (
          <FeedbackSwimlane filters={activeFilters} search={search} />
        )}
        {viewMode === "compact" && (
          <CompactView filters={activeFilters} search={search} />
        )}
      </div>
    </div>
  );
}

function CompactView({
  filters,
  search,
}: {
  filters: Set<string>;
  search: string;
}) {
  const messages = useFeedbackStore((s) => s.messages);

  const filtered = messages.filter((msg) => {
    if (filters.size > 0 && !filters.has(msg.type)) return false;
    if (search) {
      const content = JSON.stringify(msg.payload).toLowerCase();
      const source = (msg.source?.name ?? "").toLowerCase();
      if (!content.includes(search.toLowerCase()) && !source.includes(search.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  if (filtered.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-text-muted">
        {messages.length === 0
          ? "Waiting for feedback..."
          : "No messages match filters"}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {filtered.map((msg) => (
        <FeedbackMessage key={msg.id} msg={msg} compact />
      ))}
    </div>
  );
}
